import type { ReactNode } from 'react';

/**
 * The two things a page says when it has a number to show, or nothing at all.
 *
 * These lived in `features/parent/ParentShell` and were imported from there by
 * teen and employer pages alike — seventeen of them. A shared primitive behind
 * one role's door is a primitive people route around, which is exactly how a
 * codebase ends up with four slightly different empty states. They belong
 * here, where no role has to reach into another's feature to render a list
 * with nothing in it.
 */

/** One headline number, with its label and an optional aside. */
export function StatTile({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: number | string;
  hint?: string;
  /** `attention` for something waiting on the reader, `good` for settled. */
  tone?: 'neutral' | 'attention' | 'good';
  icon?: ReactNode;
}) {
  const tones = {
    neutral: 'border-line bg-white',
    attention: 'border-sun-200 bg-sun-50',
    good: 'border-safe-200 bg-safe-50',
  } as const;

  const valueTones = {
    neutral: 'text-ink',
    attention: 'text-sun-700',
    good: 'text-safe-700',
  } as const;

  return (
    <div className={`rounded-2xl border p-5 shadow-soft ${tones[tone]}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        {icon}
      </div>
      {/* Tabular figures so a row of tiles does not jitter as counts change. */}
      <p className={`mt-2 text-3xl font-extrabold tabular-nums ${valueTones[tone]}`}>{value}</p>
      {hint && <p className="mt-1 text-xs leading-relaxed text-slate-500">{hint}</p>}
    </div>
  );
}

/**
 * What a page says when a list is empty.
 *
 * Three parts, all of them load-bearing: what is not here, why that might be,
 * and the one action that would change it. An empty box with no explanation
 * reads as a bug.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-brand-50 text-brand-600">
        {icon}
      </span>
      <h2 className="mt-4 text-base font-bold text-ink">{title}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">{children}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
